// Which SUBJECT a notification type is about, for the icon and badge.
//
// ⚠️ Mirrors the prefixes in notification-service `internal/prefs/catalog.go`.
// Kept as a pure function because the UI previously keyed off display
// SEVERITIES ("info"/"warning"/"success"), and when 85 call sites were retyped
// to real subjects on 2026-10-05 every one of them would have dropped to the
// generic grey bell. The severities stay mapped below: rows stored before the
// retyping still carry them, and they must keep rendering as they always did.
//
// Order matters the same way it does in the backend catalog: 'approvals'
// matches by substring and would otherwise steal payroll types containing
// "approval", so the prefix rules are checked before it.
export type NotificationSubject =
  | 'work' | 'leave' | 'expenses' | 'payroll' | 'recognition'
  | 'conversations' | 'learning' | 'approvals'
  // The seven categories added 2026-10-06 for the 45 severity-typed
  // notifications. Without these they would all collapse to the grey bell.
  | 'finance' | 'performance' | 'surveys' | 'documents' | 'calendar'
  | 'integrations' | 'people'
  | null;

export const notificationSubject = (type: string): NotificationSubject => {
  const t = (type || '').toLowerCase().trim();
  if (!t) return null;
  const starts = (...p: string[]) => p.some((x) => t.startsWith(x));
  if (starts('work_')) return 'work';
  if (starts('leave')) return 'leave';
  if (starts('expense')) return 'expenses';
  if (starts('payroll', 'payslip')) return 'payroll';
  if (starts('recognition', 'kudos')) return 'recognition';
  if (starts('conversation', 'message')) return 'conversations';
  if (starts('learning', 'course', 'training')) return 'learning';
  if (t.includes('change_request') || t.includes('approval')) return 'approvals';
  // Order mirrors the backend catalog, which places these AFTER the original
  // eight so they can never re-route an existing type.
  if (starts('loan', 'credit', 'advance', 'envelope', 'esign', 'settlement', 'payment_method')) return 'finance';
  if (starts('performance', 'goal', 'one_on_one', 'feedback', 'ai_insight', 'connected_progress')) return 'performance';
  if (starts('survey')) return 'surveys';
  if (starts('document')) return 'documents';
  if (starts('meeting', 'calendar')) return 'calendar';
  if (starts('integration', 'email_')) return 'integrations';
  if (starts('hr_action', 'lifecycle', 'holiday', 'weekly_digest', 'referral', 'music')) return 'people';
  return null;
};

// A few types are distinct enough to deserve their own glyph INSIDE their
// subject (category, badge and preferences are still the subject's).
export type NotificationGlyph = 'paperclip' | 'file-up';
export const notificationTypeGlyph = (type: string): NotificationGlyph | null => {
  switch ((type || '').toLowerCase().trim()) {
    case 'work_attachment_added': return 'paperclip';
    case 'work_attachment_version_added': return 'file-up';
    default: return null;
  }
};
